import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Trophy, TrendingUp, Target, CheckSquare, Loader2, ArrowUpDown } from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];
const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 0 })}`;
const ROLE_LABEL = {
  admin: "Administrador", ceo: "CEO", director: "Director", ventas: "Ventas", cuentas: "Cuentas",
  contador: "Contador", marketing: "Marketing", produccion: "Producción", operaciones: "Operaciones",
  disenador: "Diseñador", interior_designer: "Diseñador", fabricante: "Fabricante", cliente: "Cliente",
};
const COLS = [
  { key: "score", label: "Score" },
  { key: "revenue", label: "Ingresos", money: true },
  { key: "pipeline", label: "Pipeline", money: true },
  { key: "leads_won", label: "Ganados" },
  { key: "projects", label: "Proyectos" },
  { key: "tasks_done", label: "Tareas ✓" },
  { key: "tasks_overdue", label: "Vencidas", warn: true },
  { key: "certificates", label: "Certif." },
];

export default function MasterMetrics() {
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("score");

  useEffect(() => { api.get("/master-metrics").then((r) => setData(r.data)).catch(() => setData({ rows: [], totals: {} })); }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    return [...data.rows].sort((a, b) => (b[sort] || 0) - (a[sort] || 0));
  }, [data, sort]);

  if (!data) return <OSLayout title="Métricas Maestras"><div className="flex items-center gap-2 text-navy/40"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div></OSLayout>;

  const top = rows.slice(0, 3);
  const t = data.totals || {};

  return (
    <OSLayout title="Métricas Maestras">
      <div className="space-y-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2">OMNIA · Dirección</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">Métricas Maestras</h2>
          <p className="mt-2 text-navy/50">Comparativo de rendimiento de todo el equipo. Visible solo para Administrador y CEO.</p>
        </motion.div>

        {/* Totals */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[["Usuarios", t.users, false], ["Ingresos", t.revenue, true], ["Pipeline", t.pipeline, true], ["Proyectos", t.projects, false], ["Tareas ✓", t.tasks_done, false]].map(([l, v, m]) => (
            <div key={l} className="bg-white border border-navy/10 rounded-lg p-5" data-testid={`master-total-${l}`}>
              <div className="overline text-navy/40 text-[0.6rem]">{l}</div>
              <div className="mt-2 text-2xl font-medium tracking-tight">{m ? money(v) : (v ?? 0)}</div>
            </div>
          ))}
        </div>

        {/* Podium */}
        {top.length > 0 && (
          <div className="grid md:grid-cols-3 gap-4" data-testid="master-podium">
            {top.map((u, i) => (
              <motion.div key={u.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease, delay: i * 0.06 }}
                className={`rounded-xl p-5 border ${i === 0 ? "bg-gradient-violet text-white border-transparent" : "bg-white border-navy/10"}`}>
                <div className="flex items-center justify-between">
                  <Trophy className={`w-5 h-5 ${i === 0 ? "text-white" : "text-gold"}`} />
                  <span className={`text-xs ${i === 0 ? "text-white/70" : "text-navy/40"}`}>#{i + 1}</span>
                </div>
                <div className={`mt-3 font-medium ${i === 0 ? "text-white" : "text-navy"}`}>{u.name}</div>
                <div className={`text-xs ${i === 0 ? "text-white/70" : "text-navy/40"}`}>{ROLE_LABEL[u.role] || u.role}</div>
                <div className="mt-3 flex items-center gap-4 text-sm">
                  <span className="inline-flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" />{money(u.revenue)}</span>
                  <span className="inline-flex items-center gap-1"><Target className="w-3.5 h-3.5" />{u.leads_won}</span>
                  <span className="inline-flex items-center gap-1"><CheckSquare className="w-3.5 h-3.5" />{u.tasks_done}</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-navy/10 rounded-lg overflow-x-auto" data-testid="master-table">
          <div className="grid grid-cols-[1.6fr_repeat(8,1fr)] gap-2 px-5 py-3 border-b border-navy/10 min-w-[1000px]">
            <span className="overline text-navy/40">Usuario</span>
            {COLS.map((c) => (
              <button key={c.key} data-testid={`master-sort-${c.key}`} onClick={() => setSort(c.key)}
                className={`overline text-left inline-flex items-center gap-1 ${sort === c.key ? "text-violet" : "text-navy/40 hover:text-navy"}`}>
                {c.label} <ArrowUpDown className="w-3 h-3" />
              </button>
            ))}
          </div>
          {rows.length === 0 ? <div className="px-5 py-10 text-center text-navy/40">Sin datos de usuarios todavía.</div> :
            rows.map((u) => (
              <div key={u.id} data-testid={`master-row-${u.id}`} className="grid grid-cols-[1.6fr_repeat(8,1fr)] gap-2 px-5 py-3 items-center border-b border-navy/5 last:border-0 text-sm min-w-[1000px]">
                <div className="min-w-0">
                  <div className="font-medium truncate">{u.name}</div>
                  <div className="text-[11px] text-navy/40">{ROLE_LABEL[u.role] || u.role}{u.status !== "active" ? ` · ${u.status}` : ""}</div>
                </div>
                {COLS.map((c) => (
                  <span key={c.key} className={`${sort === c.key ? "font-semibold text-violet" : c.warn && u[c.key] > 0 ? "text-destructive" : "text-navy/80"}`}>
                    {c.money ? money(u[c.key]) : (u[c.key] ?? 0)}
                  </span>
                ))}
              </div>
            ))}
        </div>
        <p className="text-[11px] text-navy/40">Score = ingresos/1000 + ganados×5 + tareas completadas×2 + certificados×3 − tareas vencidas. Los ingresos se atribuyen al responsable (owner) del proyecto/campaña.</p>
      </div>
    </OSLayout>
  );
}
